import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, X } from 'lucide-react';
import { isPastDeadline, normalizeAlternatives } from '@/lib/approvalsData';
import ApprovalEditor, { changedEdits, validateEdits } from '@/components/dashboard/ApprovalEditor';

/* One pending approval, drawn the same way in the inbox page and in Milli's
 * console: Milli's case for the decision, the thing itself (post, email,
 * article) with edit-then-approve, design previews, and the two buttons.
 * `onDecide(row, 'approved' | 'rejected' | 'modified', edits)` is the
 * caller's; the confirm dialog and the RPC live there. */

export const formatWhen = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

function DetailRow({ label, children }) {
  return (
    <div className="text-sm">
      <span className="uppercase tracking-[0.14em] text-[11px] text-muted-foreground mr-2">
        {label}
      </span>
      <span className="text-foreground">{children}</span>
    </div>
  );
}

export default function ApprovalCard({ row, previews, postPreview, busy, onDecide, t, compact = false }) {
  const late = isPastDeadline(row);
  const alternatives = normalizeAlternatives(row.alternatives);
  const shots = Array.isArray(previews) ? previews : [];
  // Edit-then-approve (072): J.O.'s edits to the draft, in the preview's
  // field names; null until he changes something.
  const [edits, setEdits] = useState(null);
  const changes = changedEdits(postPreview, edits);
  const problem = validateEdits(postPreview, edits, t);

  return (
    <Card
      className={late ? 'border-[hsl(var(--destructive))]/50' : undefined}
      data-testid={`approval-card-${row.id}`}
    >
      <CardHeader className={compact ? 'p-4 pb-2' : 'pb-3'}>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{row.business}</Badge>
          <Badge variant="outline" className="text-muted-foreground">
            {row.department}
          </Badge>
          {late ? (
            <Badge className="bg-red-500/15 text-red-600 border-red-500/30">
              {t('approvals.pastDeadline')}
            </Badge>
          ) : null}
          <span className="ml-auto text-xs text-muted-foreground">
            {formatWhen(row.created_at)}
          </span>
        </div>
        <CardTitle className={`${compact ? 'text-sm' : 'text-base'} leading-snug pt-1`}>
          {row.decision_needed}
        </CardTitle>
      </CardHeader>
      <CardContent className={compact ? 'p-4 pt-0 space-y-2' : 'space-y-2.5'}>
        {/* The post, email or article itself, as it will be seen, and the
            place to change its words before approving. Milli's notes about
            it follow below. */}
        <ApprovalEditor preview={postPreview} edits={edits} onChange={setEdits} t={t} />
        {shots.length ? (
          <div data-testid={`approval-previews-${row.id}`}>
            <span className="uppercase tracking-[0.14em] text-[11px] text-muted-foreground">
              {t('approvals.fields.previews')}
            </span>
            <div className={`mt-1.5 grid gap-2 ${compact ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
              {shots.map((s) => (
                <a
                  key={s.path}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={t('approvals.fields.openFull')}
                  className="group block rounded-md border border-border overflow-hidden bg-muted/40"
                >
                  <img
                    src={s.url}
                    alt={t('approvals.fields.version', { n: s.variant })}
                    loading="lazy"
                    className="block w-full aspect-square object-contain bg-[#1e2023]"
                  />
                  <div className="px-2 py-1 text-[11px] text-muted-foreground flex justify-between">
                    <span>{t('approvals.fields.version', { n: s.variant })}</span>
                    {s.width && s.height ? (
                      <span>
                        {s.width}×{s.height}
                      </span>
                    ) : null}
                  </div>
                </a>
              ))}
            </div>
          </div>
        ) : null}
        <DetailRow label={t('approvals.fields.recommended')}>{row.recommended_action}</DetailRow>
        <DetailRow label={t('approvals.fields.reason')}>{row.reason}</DetailRow>
        <DetailRow label={t('approvals.fields.exposure')}>{row.exposure}</DetailRow>
        <DetailRow label={t('approvals.fields.noAction')}>{row.no_action_consequence}</DetailRow>
        {row.deadline ? (
          <DetailRow label={t('approvals.fields.deadline')}>{formatWhen(row.deadline)}</DetailRow>
        ) : null}
        {alternatives.length ? (
          <div className="text-sm">
            <span className="uppercase tracking-[0.14em] text-[11px] text-muted-foreground">
              {t('approvals.fields.alternatives')}
            </span>
            <ul className="mt-1 list-disc pl-5 space-y-0.5 text-foreground">
              {alternatives.map((alt, i) => (
                <li key={i}>{alt}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="flex gap-2 pt-2">
          <Button
            size="sm"
            disabled={busy || !!problem}
            onClick={() => (changes ? onDecide(row, 'modified', changes) : onDecide(row, 'approved'))}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`approval-approve-${row.id}`}
          >
            <Check size={14} className="mr-1.5" />
            {changes ? t('approvals.approveEdited') : t('approvals.approve')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onDecide(row, 'rejected')}
            className="border-red-500/40 text-red-600 hover:bg-red-500/10"
            data-testid={`approval-reject-${row.id}`}
          >
            <X size={14} className="mr-1.5" />
            {t('approvals.reject')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
